/* eslint-disable */
// AIレポート機能のAPIクライアント (保護者モバイル / CCS HOME)。
// モック/実APIは VUE_APP_AI_REPORT_USE_MOCK で切替可能。
// 保護者側は `status=published` のレポートのみ取得可能。

import ApiClient from "@/api/ApiClient";

// =====================================================================
// 型定義
// =====================================================================

export type AiReportQuarter = 1 | 2 | 3 | 4;

export interface AiReportListItem {
  childId: number;
  childLastName: string;
  childFirstName: string;
  businessYear: number;
  quarter: AiReportQuarter;
  monthsLabel: string;
  // タイトル例: "2歳11ヶ月のVEVOレポート 2026年12月時点"
  title: string;
  publishedAt: string;
  dataCoverageMonths: number[];
}

export interface AiReportDetail extends AiReportListItem {
  content: string;
}

// =====================================================================
// モックデータ
// =====================================================================

// VUE_APP_AI_REPORT_USE_MOCK='true' を設定した環境でのみモック応答に切り替える。
// 未設定時はデフォルトで実APIに疎通する。
const USE_MOCK = process.env.VUE_APP_AI_REPORT_USE_MOCK === 'true';

const SAMPLE_CONTENT_Q3 = `こんにちは、保育ロボットのVEVOです。
太郎君の10月から12月の成長について、とてもポジティブな変化がありましたのでお伝えします。

まず、身体・運動面での成長が顕著に見られました。11月には、なわとびで連続して高くジャンプできるようになり、片足で10秒以上立つこともできるようになっています。これは、バランス感覚と脚力の向上を示しています。また、手先の器用さも向上し、小さなボタンのかけ外しや細かい作業もスムーズにこなせるようになりました。12月には、はさみやクレヨンを使って複雑な形を切ったり、細かく塗り分けたりすることができるようになり、創造的な活動の幅が広がりました。
これらの成長は、太郎君の運動能力と手先の器用さが急速に発達していることを示しています。

認知・思考面では、10月に複雑な因果関係を理解し始め、11月には微細な変化に気づく観察力が身につきました。12月には、自分で見通しを持って計画的に課題を解決する能力が発達し、問題解決力が大きく向上しました。特に、ブロック遊びでの創造性の高さは注目に値します。複雑な線路や巨大な恐竜の化石を再現する能力は、空間認識能力と創造力の高さを示しています。これは、将来的に建築や工学分野での才能につながる可能性があります。

社会性・情緒面でも素晴らしい成長が見られました。10月には感情を適切に表現できるようになり、11月には順番やルールを長時間守れるようになりました。12月には、・・・・

最後に、あきら君の成長を俳句にまとめてみました。
恐竜と 電車を積んで 夢広がる
きょうりゅうと でんしゃをつんで ゆめひろがる`;

const SAMPLE_CONTENT_Q2 = `こんにちは、保育ロボットのVEVOです。
太郎君の7月から9月の成長について保護者の皆様にお伝えします。

夏の時期を通して、太郎君は身体活動の幅が大きく広がりました。水遊びでは積極的に挑戦し、走る・跳ぶ・登るといった基本動作が一段と安定しました。とくに集団でのルール遊びでは、自分から「次は誰の番？」と気にかける場面が増え、お友達への気配りが芽生えています。

認知面では、数や量の概念が確かなものになりました。10以上の数を数えるだけでなく、簡単な足し算引き算にも挑戦するようになり、考える喜びを感じている様子が伝わってきます。

社会性の面では、集団活動の中で自分の意見を伝える力が伸びました。「やってみたい」「もう一度やりたい」と自分の気持ちを言葉にすることが増え、仲間との関係性がより豊かになっています。

夏の経験は、太郎君にとって挑戦と発見の連続だったようです。これからも好奇心を大切に、伸び伸びと過ごしていけるよう支えてまいります。

夏の風 笑顔こぼれる プールかな
なつのかぜ えがおこぼれる プールかな`;

const SAMPLE_CONTENT_Q1 = `こんにちは、保育ロボットのVEVOです。
太郎君の4月から6月の成長について保護者の皆様にお伝えします。

新年度が始まり、新しいクラスにもすっかり慣れた様子の太郎君。最初は少し緊張も見られましたが、5月の終わりごろには自分から友達を誘って遊ぶ姿が見られるようになりました。

身体面では、平均台や障害物を安定して渡れるようになり、リズム運動でも音楽に合わせて複雑な動作ができるようになっています。

生活面では、食事のマナーがしっかり身につき、自分から「いただきます」を言う姿が印象的です。手洗いや片付けも、自分のタイミングで行えるようになりました。

これからの成長がますます楽しみです。

新緑に 笑顔ふくらむ 春の園
しんりょくに えがおふくらむ はるのその`;

interface MockReportEntry {
  childId: number;
  childLastName: string;
  childFirstName: string;
  ageYears: number;
  ageMonths: number;
  birthYearMonth: string;
  reports: Array<{
    businessYear: number;
    quarter: AiReportQuarter;
    monthsLabel: string;
    titleSnapshotYearMonth: string; // 例 "2026年12月"
    titleAgeMonthsAtSnapshot: number; // 例 35 (=2歳11ヶ月)
    publishedAt: string; // ISO
    dataCoverageMonths: number[];
    content: string;
  }>;
}

// 子供ID -> モックレポート
const MOCK_REPORTS: Record<number, MockReportEntry> = {
  101: {
    childId: 101,
    childLastName: "貞松",
    childFirstName: "太郎",
    ageYears: 2,
    ageMonths: 11,
    birthYearMonth: "2023-12",
    reports: [
      {
        businessYear: 2026,
        quarter: 3,
        monthsLabel: "10-12月",
        titleSnapshotYearMonth: "2026年12月",
        titleAgeMonthsAtSnapshot: 35,
        publishedAt: new Date(Date.now() - 86400_000 * 2).toISOString(),
        dataCoverageMonths: [10, 11, 12],
        content: SAMPLE_CONTENT_Q3,
      },
      {
        businessYear: 2026,
        quarter: 2,
        monthsLabel: "7-9月",
        titleSnapshotYearMonth: "2026年9月",
        titleAgeMonthsAtSnapshot: 32,
        publishedAt: new Date(Date.now() - 86400_000 * 60).toISOString(),
        dataCoverageMonths: [7, 8, 9],
        content: SAMPLE_CONTENT_Q2,
      },
      {
        businessYear: 2026,
        quarter: 1,
        monthsLabel: "4-6月",
        titleSnapshotYearMonth: "2026年6月",
        titleAgeMonthsAtSnapshot: 29,
        publishedAt: new Date(Date.now() - 86400_000 * 120).toISOString(),
        dataCoverageMonths: [4, 6], // 5月分のデータが不足しているケース
        content: SAMPLE_CONTENT_Q1,
      },
    ],
  },
};

function formatAge(ageMonths: number): string {
  const years = Math.floor(ageMonths / 12);
  const months = ageMonths % 12;
  if (months === 0) return `${years}歳`;
  return `${years}歳${months}ヶ月`;
}

function buildItem(
  entry: MockReportEntry,
  r: MockReportEntry["reports"][number],
  detail: boolean,
): AiReportDetail | AiReportListItem {
  const title = `${formatAge(r.titleAgeMonthsAtSnapshot)}のVEVOレポート ${r.titleSnapshotYearMonth}時点`;
  const base: AiReportListItem = {
    childId: entry.childId,
    childLastName: entry.childLastName,
    childFirstName: entry.childFirstName,
    businessYear: r.businessYear,
    quarter: r.quarter,
    monthsLabel: r.monthsLabel,
    title,
    publishedAt: r.publishedAt,
    dataCoverageMonths: r.dataCoverageMonths,
  };
  if (detail) {
    return { ...base, content: r.content };
  }
  return base;
}

function delay<T>(value: T, ms = 250): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

// =====================================================================
// APIクライアント
// =====================================================================

export default class ApiAiReport {
  /**
   * 公開済AIレポートの一覧を取得する。
   */
  static async listPublished({
    childId,
  }: {
    childId: number;
  }): Promise<AiReportListItem[]> {
    if (USE_MOCK) {
      const entry = MOCK_REPORTS[childId] ?? Object.values(MOCK_REPORTS)[0];
      if (!entry) return delay([]);
      const items = entry.reports.map((r) => buildItem(entry, r, false) as AiReportListItem);
      // 新しい順で返す
      return delay(items.sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1)));
    }
    const response = await ApiClient.get(
      `/child-development/ai-report/parents/children/${childId}`,
      null,
    );
    return response as AiReportListItem[];
  }

  /**
   * 個別の公開済AIレポートを取得する。
   */
  static async getPublished({
    childId,
    businessYear,
    quarter,
  }: {
    childId: number;
    businessYear: number;
    quarter: AiReportQuarter;
  }): Promise<AiReportDetail | null> {
    if (USE_MOCK) {
      const entry = MOCK_REPORTS[childId] ?? Object.values(MOCK_REPORTS)[0];
      if (!entry) return delay(null);
      const report = entry.reports.find(
        (r) => r.businessYear === businessYear && r.quarter === quarter,
      );
      if (!report) return delay(null);
      return delay(buildItem(entry, report, true) as AiReportDetail);
    }
    const response = await ApiClient.get(
      `/child-development/ai-report/parents/children/${childId}/${businessYear}/${quarter}`,
      null,
    );
    return response as AiReportDetail;
  }
}
